import {
  type QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { v4 as uuid } from 'uuid'
import { Toast } from '@skop-app/lib-front'

import { getAmplifyClient } from '@/services/amplifyClient'
import { createOrderMutation, updateOrderMutation } from '@/services/graphql'
import { createMessage } from '@/services/mutations'
import { getGraphQLErrorMessage } from '@/utils/helpers'
import { normalizePlainTextMessage } from '@/utils/normalizePlainTextMessage'
import { trackUsageEvent } from '@/utils/posthog.utils'

const client = getAmplifyClient()

export type SilentMutationOptions = {
  meta: {
    silent?: boolean
  }
}

type UpdateOrderMutationInput = {
  id?: string
  status?: string
  [key: string]: unknown
}

type UpdateOrderMutationContext = {
  previousOrders: Array<[QueryKey, unknown]>
  previousRequests: Array<[QueryKey, unknown]>
  previousOrder: unknown
  orderID?: string
}

function toRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function isOrderWithID(value: unknown, orderID: string): boolean {
  const record = toRecord(value)
  return Boolean(
    record && typeof record.id === 'string' && record.id === orderID,
  )
}

function patchOrdersArray(
  orders: unknown[],
  input: UpdateOrderMutationInput,
  orderID: string,
): unknown[] {
  if (input.status === 'DELETED') {
    return orders.filter((order) => !isOrderWithID(order, orderID))
  }

  return orders.map((order) => {
    const record = toRecord(order)
    if (!record || !isOrderWithID(order, orderID)) return order
    return {
      ...record,
      ...input,
    }
  })
}

function patchOrdersCacheData(
  cachedData: unknown,
  input: UpdateOrderMutationInput,
): unknown {
  const orderID = typeof input.id === 'string' ? input.id : undefined
  if (!orderID) return cachedData

  if (Array.isArray(cachedData)) {
    return patchOrdersArray(cachedData, input, orderID)
  }

  const cachedRecord = toRecord(cachedData)
  if (!cachedRecord) return cachedData

  const pages = cachedRecord.pages
  if (!Array.isArray(pages)) return cachedData

  const nextPages = pages.map((page) => {
    const pageRecord = toRecord(page)
    if (!pageRecord || !Array.isArray(pageRecord.items)) return page

    return {
      ...pageRecord,
      items: patchOrdersArray(pageRecord.items, input, orderID),
    }
  })

  return {
    ...cachedRecord,
    pages: nextPages,
  }
}

export const useUpdateOrder = () => {
  const queryClient = useQueryClient()
  const t = useTranslations('Order')
  return useMutation({
    mutationKey: ['updateOrder'],
    mutationFn: updateOrder,
    onMutate: async (variables): Promise<UpdateOrderMutationContext> => {
      const input = (variables?.input ?? {}) as UpdateOrderMutationInput
      const orderID = typeof input.id === 'string' ? input.id : undefined

      await Promise.all([
        queryClient.cancelQueries({
          queryKey: ['request'],
          exact: false,
        }),
        queryClient.cancelQueries({
          queryKey: ['orders'],
          exact: false,
        }),
        ...(orderID
          ? [
              queryClient.cancelQueries({
                queryKey: ['order', orderID],
              }),
            ]
          : []),
      ])

      const previousOrders = queryClient.getQueriesData({
        queryKey: ['orders'],
        exact: false,
      })
      const previousRequests = queryClient.getQueriesData({
        queryKey: ['request'],
        exact: false,
      })
      const previousOrder = orderID
        ? queryClient.getQueryData(['order', orderID])
        : undefined

      queryClient.setQueriesData(
        {
          queryKey: ['orders'],
          exact: false,
        },
        (cachedData) => patchOrdersCacheData(cachedData, input),
      )

      queryClient.setQueriesData(
        {
          queryKey: ['request'],
          exact: false,
        },
        (cachedData) => patchOrdersCacheData(cachedData, input),
      )

      if (orderID) {
        queryClient.setQueryData(['order', orderID], (cachedData: unknown) => {
          const record = toRecord(cachedData)
          if (!record) return cachedData
          return {
            ...record,
            ...input,
          }
        })
      }

      return {
        previousOrders,
        previousRequests,
        previousOrder,
        orderID,
      }
    },
    onError: (e: unknown, _variables, context) => {
      if (context) {
        context.previousOrders.forEach(([queryKey, previousData]) => {
          queryClient.setQueryData(queryKey, previousData)
        })
        context.previousRequests.forEach(([queryKey, previousData]) => {
          queryClient.setQueryData(queryKey, previousData)
        })
        if (context.orderID) {
          queryClient.setQueryData(
            ['order', context.orderID],
            context.previousOrder,
          )
        }
      }

      const error = e as { message?: string }
      if (error?.message === 'Some products are not available') {
        Toast.error(t('insufficientStock'), {
          id: 'insufficientStock',
          closeButton: true,
          duration: 7000,
        })
      }
    },
    onSettled: (_data, _error, variables) => {
      const orderID =
        typeof variables?.input?.id === 'string'
          ? variables.input.id
          : undefined

      queryClient.invalidateQueries({
        queryKey: ['request'],
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: ['orders'],
        exact: false,
      })
      if (orderID) {
        queryClient.invalidateQueries({
          queryKey: ['order', orderID],
        })
      }
    },
    meta: { silent: true },
  })
}

export const updateOrder = async ({ input }) => {
  try {
    const result = await client.graphql({
      query: updateOrderMutation,
      variables: {
        input,
      },
    })
    const response = result as {
      data?: { updateOrder?: unknown }
      errors?: Array<{ message?: string }>
    }
    if (response.errors?.length) {
      throw new Error(response.errors[0].message ?? 'Update order failed')
    }
    const hasStatusUpdate = typeof input.status === 'string'
    trackUsageEvent('feature_used', {
      feature: 'orders',
      action: hasStatusUpdate
        ? input.status === 'DELETED'
          ? 'delete_order'
          : 'update_order_status'
        : 'update_order',
      surface: 'orders',
      result: 'success',
      ...(hasStatusUpdate ? { status_action: input.status as string } : {}),
    })
    return result
  } catch (e: unknown) {
    console.error(e)
    throw new Error(getGraphQLErrorMessage(e))
  }
}

export const useCreateOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createOrder'],
    mutationFn: createOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['request'],
        exact: false,
      })

      queryClient.invalidateQueries({
        queryKey: ['orders'],
        exact: false,
      })
    },
    meta: { silent: true },
  })
}

type CreateOrderInput = {
  id: string
  message?: string
  orderUserId: string
  toUserID: string | undefined
  requestType: string
  [key: string]: unknown
}

export const createOrder = async ({ input }: { input: CreateOrderInput }) => {
  try {
    const normalizedMessage =
      typeof input?.message === 'string'
        ? normalizePlainTextMessage(input.message)
        : ''

    const result = await client.graphql<{
      createOrder?: { id: string } | null
    }>({
      query: createOrderMutation,
      variables: {
        input: normalizedMessage
          ? { ...input, message: normalizedMessage }
          : input,
      },
    })
    if (result.errors?.length) {
      throw Object.assign(
        new Error(
          result.errors.map((error) => error.message).join('; ') ||
            'Create order failed',
        ),
        { errors: result.errors },
      )
    }
    if (result.data?.createOrder?.id !== input.id) {
      throw new Error('Create order returned no matching result')
    }
    if (normalizedMessage !== '' && input.orderUserId && input.toUserID) {
      createMessage({
        input: {
          id: uuid(),
          orderID: input.id,
          userID: input.orderUserId,
          toUserID: input.toUserID,
          message: normalizedMessage,
          type: input.requestType,
          status: 'PENDING',
        },
      })
    }
    trackUsageEvent('feature_used', {
      feature: 'orders',
      action: input.requestType === 'QUOTE' ? 'create_quote' : 'create_order',
      surface: 'orders',
      result: 'success',
      order_type: input.requestType,
    })
  } catch (e) {
    console.error('FROM CREATE ORDER', e)
    trackUsageEvent('feature_used', {
      feature: 'orders',
      action: input.requestType === 'QUOTE' ? 'create_quote' : 'create_order',
      surface: 'orders',
      result: 'failure',
      order_type: input.requestType,
    })
    throw e
  }
}
